import React, { useState } from 'react';
import { Shield, Mail, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import api from '../../utils/api';
import { createPortal } from 'react-dom';

const Loader = () => createPortal(
  <div className="fixed inset-0 bg-black/60 z-[999] flex items-center justify-center">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-lime-400"></div>
    <p className="text-white ml-4">Updating password...</p>
  </div>,
  document.body
);

const EmailLoginSecurityScreen = ({ onBack, user }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [banner, setBanner] = useState({ message: '', type: '', show: false });

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const showBanner = (message, type) => {
    setBanner({ message, type, show: true });
    if (type === 'success') {
      setTimeout(() => {
        setBanner({ message: '', type: '', show: false });
      }, 4000);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setBanner({ message: '', type: '', show: false });

    if (!currentPassword || !newPassword || !confirmPassword) {
      showBanner('All password fields are required.', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showBanner('New passwords do not match.', 'error');
      return;
    }

    if (newPassword.length < 6) {
      showBanner('New password must be at least 6 characters long.', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post('/api/auth/change-password', {
        currentPassword,
        newPassword
      });

      setIsLoading(false);
      if (response.status === 200) {
        showBanner('Password updated successfully!', 'success');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        showBanner(response.data?.message || 'Failed to update password.', 'error');
      }
    } catch (err) {
      setIsLoading(false);
      const errMsg = err.response?.data?.message || 'Error occurred. Please try again.';
      showBanner(errMsg, 'error');
    }
  };

  return (
    <div className="max-w-[480px] mx-auto pt-2 pb-4">
      <style>{`
        @keyframes security-slideIn {
          from { opacity: 0; transform: translate(-50%, -10px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
      {isLoading && <Loader />}

      {/* Banner */}
      {banner.show && (
        <div
          className={`fixed top-2.5 left-1/2 -translate-x-1/2 z-[1000] w-[90%] max-w-[380px] p-3.5 rounded-[12px] border backdrop-blur-[8px] ${
            banner.type === 'success'
              ? 'bg-green-500/15 border-green-500/40 text-green-200'
              : 'bg-red-500/15 border-red-500/40 text-red-200'
          }`}
          style={{ animation: 'security-slideIn 0.3s ease-out' }}
        >
          <div className="flex items-center gap-2">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${
              banner.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
            }`}>
              {banner.type === 'success' ? '✓' : '✕'}
            </div>
            <span className="text-[13px] font-medium flex-1">{banner.message}</span>
            <button
              onClick={() => setBanner({ message: '', type: '', show: false })}
              className="bg-transparent border-none text-white/70 cursor-pointer p-0.5 rounded-full text-[12px] transition-all hover:bg-white/15 hover:text-white"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Top Bar */}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={onBack}
          className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all"
        >
          <ArrowLeft size={18} />
        </button>
        <span className="text-[20px] font-medium text-white">Email & Login Security</span>
      </div>

      {/* Intro Header */}
      <div className="flex flex-col items-center gap-2.5 mb-6 py-4 px-0">
        <div className="w-12 h-12 rounded-[12px] bg-lime-500/10 border border-lime-500/20 flex items-center justify-center">
          <Shield size={24} className="text-[#6CD800]" />
        </div>
        <span className="text-[14px] text-gray-400 text-center px-4">
          Manage your login email and keep your account secure by updating your password.
        </span>
      </div>

      {/* Email Section */}
      <div className="text-[11px] font-medium text-[#888] uppercase tracking-[0.6px] mb-1.5">Registered Email</div>
      <div className="bg-[#101010] border-[0.5px] border-[#2b2b2b] rounded-[12px] p-3.5 mb-5 flex items-center gap-3">
        <div className="w-8 h-8 rounded-[8px] bg-[#181818] flex items-center justify-center text-gray-400">
          <Mail size={18} />
        </div>
        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-[13px] font-medium text-white truncate">{user?.email || 'No email registered'}</span>
          <span className="text-[11px] text-[#888] mt-0.5">Used for login and notifications</span>
        </div>
      </div>

      {/* Change Password Form */}
      <div className="text-[11px] font-medium text-[#888] uppercase tracking-[0.6px] mb-1.5">Change Password</div>
      <form onSubmit={handlePasswordChange} className="bg-[#101010] border-[0.5px] border-[#2b2b2b] rounded-[12px] overflow-hidden p-3.5 space-y-4">
        {/* Current Password */}
        <div>
          <label className="block text-[11px] font-medium text-[#888] uppercase tracking-[0.4px] mb-1">Current Password</label>
          <div className="relative">
            <input
              type={showCurrent ? 'text' : 'password'}
              className="w-full bg-[#181818] border-[0.5px] border-[#2b2b2b] rounded-[8px] p-2.5 pr-10 text-[14px] text-white outline-none transition-colors focus:border-[#6CD800]"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors bg-transparent border-none cursor-pointer"
            >
              {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div>
          <label className="block text-[11px] font-medium text-[#888] uppercase tracking-[0.4px] mb-1">New Password</label>
          <div className="relative">
            <input
              type={showNew ? 'text' : 'password'}
              className="w-full bg-[#181818] border-[0.5px] border-[#2b2b2b] rounded-[8px] p-2.5 pr-10 text-[14px] text-white outline-none transition-colors focus:border-[#6CD800]"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min. 6 chars)"
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors bg-transparent border-none cursor-pointer"
            >
              {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-[11px] font-medium text-[#888] uppercase tracking-[0.4px] mb-1">Confirm New Password</label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              className="w-full bg-[#181818] border-[0.5px] border-[#2b2b2b] rounded-[8px] p-2.5 pr-10 text-[14px] text-white outline-none transition-colors focus:border-[#6CD800]"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors bg-transparent border-none cursor-pointer"
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {/* Action Button */}
        <button
          type="submit"
          className="w-full bg-[#6CD800] text-[#181f10] border-none rounded-[10px] p-3 text-[14px] font-semibold cursor-pointer transition-opacity hover:opacity-90"
        >
          Update Password
        </button>
      </form>
    </div>
  );
};

export default EmailLoginSecurityScreen;
